import { Component, Inject, OnInit } from '@angular/core'
import { debounceTime, distinctUntilChanged, filter, map, pairwise, switchMap, take, tap } from 'rxjs/operators'
import { Store, select } from '@ngrx/store'
import * as R from 'ramda'
import { round, sumBy, cloneDeep } from 'lodash'
import * as fromRoot from '@tv3/store/state'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { getBookingGuestsCount, Inquiry } from '@tv3/store/inquiry/inquiry.model'
import {
  AccountGuard,
  AttachmentsGuard,
  DataCheckerService,
  Destroyable,
  getNightsBetweenArrivalDepart,
  Rental,
  SaveForm,
  selectRentalById,
  selectSomeOnce,
  Toaster,
  epochToViewUTC,
  TaxV2,
  isSomething,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { InvoiceNotesChangeEvent } from '@tv3/containers/invoices/invoices/add-invoice/invoice-notes/invoice-notes.component'
import { InvoiceDetailsChangeEvent } from '@tv3/containers/invoices/invoices/add-invoice/invoice-details/invoice-details.component'
import {
  AddInvoice,
  AddInvoiceComplete,
  Invoice,
  InvoiceItem,
  InvoiceLineItemTitles,
  invoiceLineItemTitlesByType,
  InvoiceStatus,
  InvoiceTypes,
  setInvoiceItemTaxV3,
  isInvoiceUpdating,
  InvoiceTotalResponse,
  InvoiceService,
  isInvoiceTotalChanged,
} from '@tokeet-frontend/invoices'
import { Actions, ofType } from '@ngrx/effects'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormGroup } from '@angular/forms'
import { convertBookingEngineTax } from '@tokeet-frontend/bookings'
import {
  AuditDiscount,
  BookingEngineBookingFee,
  BookingEngineTax,
  isExternalBookingSource,
  TaxTypes,
  InquiryCost,
} from '@tokeet/cost-resolver'
import { BehaviorSubject, combineLatest } from 'rxjs'

export interface CreateInvoiceParams {
  type: InvoiceTypes
  inquiryId: string
  isCopy?: boolean
  invoice?: Invoice
}

@Component({
  selector: 'app-add-invoice-dialog',
  templateUrl: './add-invoice-dialog.component.html',
  styleUrls: ['./add-invoice-dialog.component.scss'],
})
export class AddInvoiceDialogComponent extends Destroyable implements OnInit {
  lineItemTitles: InvoiceLineItemTitles
  invoiceStatuses = InvoiceStatus

  invoiceType: number = InvoiceTypes.BookingInvoice

  isInvoiceUpdating$ = this.store.pipe(select(isInvoiceUpdating))

  inquiry: Inquiry
  rental: Rental

  form = new FormGroup({})
  invoice = new Invoice()
  get guests() {
    return (this.inquiry && (this.inquiry.numAdults || 0) + (this.inquiry.numChild || 0)) || 1
  }

  refreshTotal$ = new BehaviorSubject<InvoiceItem[]>([])
  invoiceTotalData: InvoiceTotalResponse
  isRefreshTotal = true

  constructor(
    public dialogRef: MatDialogRef<AddInvoiceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateInvoiceParams,
    private inquiryChargeService: InquiryChargeService,
    private inquiryService: InquiryService,
    private invoiceService: InvoiceService,
    private toaster: Toaster,
    private dataCheckerService: DataCheckerService,
    private store: Store<fromRoot.State>,
    private actions$: Actions
  ) {
    super()
    this.dataCheckerService.check([AccountGuard, AttachmentsGuard])
    this.invoiceType = this.data.type
    this.invoice.invoiceItems = []

    if (this.data.isCopy) {
      this.invoice = this.data.invoice
    }
  }

  ngOnInit() {
    this.inquiryService
      .load(this.data.inquiryId)
      .pipe(
        switchMap((inquiry) => {
          this.inquiry = inquiry
          return combineLatest([
            this.inquiryChargeService.getCharges(inquiry),
            this.store.pipe(selectSomeOnce(selectRentalById(inquiry.rental.id))),
          ]).pipe(map(([charges, rental]) => ({ inquiry, charges, rental })))
        }),
        tap(({ inquiry, rental, charges }) => {
          if (!this.data.isCopy) {
            this.invoice.invoiceItems = [
              this.convertInquiryToInvoiceItem(inquiry, charges),
              ...this.convertBookingFeesToInvoiceItems(inquiry),
              ...this.convertDiscountsToInvoiceItems(inquiry),
            ]
          }
          this.initializeInvoice(inquiry, rental)
        })
      )
      .subscribe(({ inquiry, rental }) => {
        this.rental = rental
        this.lineItemTitles = invoiceLineItemTitlesByType[this.invoiceType]
        this.refreshTotal$.next(this.invoice?.invoiceItems)
      })

    this.refreshTotal$
      .pipe(
        filter(() => !!this.inquiry),
        map((data) => cloneDeep(data)),
        distinctUntilChanged((prev, next) => !isInvoiceTotalChanged(prev, next)),
        debounceTime(250),
        tap(() => (this.isRefreshTotal = true)),
        switchMap((items: InvoiceItem[]) =>
          this.invoiceService.prepare({
            line_items: items || [],
            days: getNightsBetweenArrivalDepart(this.inquiry?.guestArrive, this.inquiry?.guestDepart),
            guests: getBookingGuestsCount(this.inquiry),
          })
        ),
        untilDestroy(this)
      )
      .subscribe((data) => {
        this.invoiceTotalData = data
        this.isRefreshTotal = false
      })
  }

  initializeInvoice(inquiry: Inquiry, rental: Rental) {
    this.invoice.guestId = inquiry.guestId
    this.invoice.guestName = inquiry.guestDetails.name
    this.invoice.rentalId = inquiry.rentalId
    this.invoice.inquiryId = inquiry.id
    this.invoice.rentalName = rental.name
    this.invoice.paymentTerms = rental.payment_terms
    this.invoice.paymentInstructions = rental.payment_instructions
    this.invoice.currency = rental.currency
    this.invoice.type = this.invoiceType
  }

  close() {
    this.dialogRef.close()
  }

  onInvoiceDetailChanged({ due, date, expires, invoiceNum, addressKey }: InvoiceDetailsChangeEvent) {
    this.invoice.dueDate = due
    this.invoice.invoiceDate = date
    this.invoice.expiresAt = expires
    this.invoice.invoiceNum = invoiceNum
    this.invoice.addressKey = addressKey
  }

  onInvoiceNotesChanged({ invoice_header, notes, paymentTerms, paymentInstructions }: InvoiceNotesChangeEvent) {
    this.invoice.invoice_header = invoice_header
    this.invoice.notes = notes
    this.invoice.paymentTerms = paymentTerms
    this.invoice.paymentInstructions = paymentInstructions
  }

  onLineItemChanged(index: number, invoiceItem: InvoiceItem) {
    const items = this.invoice.invoiceItems
    items[index] = invoiceItem
    this.invoice.invoiceItems = [...items]
    this.refreshTotal$.next(items)
  }

  onRemoveLineItem(index: number) {
    const items = this.invoice.invoiceItems
    items.splice(index, 1)
    this.invoice.invoiceItems = [...items]
    this.refreshTotal$.next(items)
  }

  trackByIndex(index: any, item: InvoiceItem) {
    return item.id || index
  }

  onAddInvoiceItem() {
    const item = new InvoiceItem()
    item.percent = 1.0
    this.invoice.invoiceItems.push(item)
  }

  private convertInquiryToInvoiceItem(inquiry: Inquiry, charges: InquiryCost): InvoiceItem {
    const item = new InvoiceItem()
    item.percent = 1.0 // 100%

    if (this.invoiceType === InvoiceTypes.GeneralInvoice) {
      return item
    }
    // only for booking invoice
    const rental = inquiry.rental
    item.item = rental.name
    item.qty = getNightsBetweenArrivalDepart(inquiry.guestArrive, inquiry.guestDepart)

    const bookingEngine = inquiry.bookingEngine

    if (!isExternalBookingSource(inquiry.inquirySource)) {
      const additionalGuestsFee = bookingEngine.extraGuests || 0
      const baseCharge = bookingEngine.base + additionalGuestsFee
      // item unit price
      item.unitCost = round(baseCharge / item.qty, 4)
      // item taxes
      item.taxes = convertBookingEngineTax(bookingEngine.taxes || [])

      // item discount
      const discountFeeTotal = sumBy(bookingEngine.discountFees, (t) => t.amount || 0)
      item.discount = round(discountFeeTotal, 2)
    } else {
      // inquiry taxes
      const additionalGuestsFee = charges.extraGuests || 0
      const baseCharge = R.pathOr(0, ['charge'], charges) + additionalGuestsFee

      item.unitCost = round(baseCharge / item.qty, 4)
      const feeSum = charges.feeSum || 0
      const discountSum = charges.discountSum || 0
      item.discount = round(feeSum + discountSum, 2)
      const taxEx = new TaxV2()
      if (isSomething(charges.taxEx)) {
        taxEx.flat = charges.taxEx?.flat || 0
        taxEx.percent = charges.taxEx?.percent || 0
      } else {
        taxEx.flat = charges.taxFee || 0
        taxEx.percent = 0
      }
      item.taxEx = taxEx
      setInvoiceItemTaxV3(item, true)
    }

    const numOfPerson = (inquiry.numAdults || 0) + (inquiry.numChild || 0)
    item.description = `${item.qty} night(s) for ${numOfPerson} guest(s) at ${rental.name} starting ${epochToViewUTC(
      inquiry.guestArrive,
      'DD - MMM - YYYY'
    )} and ending ${epochToViewUTC(inquiry.guestDepart, 'DD - MMM - YYYY')}`

    return item
  }

  private convertDiscountsToInvoiceItems(inquiry: Inquiry): InvoiceItem[] {
    if (isExternalBookingSource(inquiry.inquirySource)) {
      return []
    }
    const bookingEngine = inquiry.bookingEngine

    const items = R.pathOr([], ['auditDiscounts'], bookingEngine)
    const taxes = R.pathOr([], ['taxes'], bookingEngine).filter((i: BookingEngineTax) => i.type === TaxTypes.Percent)
    return R.map((data: AuditDiscount) => {
      const item = new InvoiceItem()
      item.qty = 1
      item.unitCost = data.disc_amount
      item.item = data.disc_type === 'lengthofstay' ? 'LOS Discount' : 'Discount Code'
      item.taxes = convertBookingEngineTax(taxes)
      return item
    }, items || [])
  }

  private convertBookingFeesToInvoiceItems(inquiry: Inquiry): InvoiceItem[] {
    if (isExternalBookingSource(inquiry.inquirySource)) {
      return []
    }
    const bookingEngine = inquiry.bookingEngine
    const items = R.pathOr([], ['bookingFees'], bookingEngine)
    const taxes = R.pathOr([], ['taxes'], bookingEngine).filter((i: BookingEngineTax) => i.type === TaxTypes.Percent)
    return R.map((data: BookingEngineBookingFee) => {
      const item = new InvoiceItem()

      item.qty = 1
      item.unitCost = data.amount
      item.item = data.name
      item.description = data.description
      item.taxes = data.taxable ? convertBookingEngineTax(taxes) : []

      return item
    }, items || [])
  }

  @SaveForm()
  onSaveInvoice(form: FormGroup) {
    const total = this.invoiceTotalData.total
    if (total <= 0) {
      this.toaster.error($localize`Invoice total must be positive number.`)
      return
    }
    this.store.dispatch(AddInvoice({ invoice: this.invoice }))
    this.actions$.pipe(ofType(AddInvoiceComplete), take(1)).subscribe(({ invoice }) => {
      this.dialogRef.close(invoice)
    })
  }
}
